"use client";

import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";
import { DayFlag, DayPicker, SelectionState, UI } from "react-day-picker";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";

export const Calendar = ({ className, classNames, showOutsideDays = true, components, ...props }) => {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks
      className={cn("p-3 relative", className)}
      classNames={{
        [UI.Months]: "flex flex-row space-x-4 w-auto",
        [UI.Month]: "space-y-4 ml-0",
        [UI.MonthCaption]: "flex justify-center items-center h-7 px-12",
        [UI.CaptionLabel]: "text-sm font-medium",
        [UI.PreviousMonthButton]: cn(
          buttonVariants({ variant: "outline" }),
          "absolute left-3 top-2 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        [UI.NextMonthButton]: cn(
          buttonVariants({ variant: "outline" }),
          "absolute right-3 top-2 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        [UI.MonthGrid]: "w-full border-collapse space-y-1",
        [UI.Weekdays]: "flex",
        [UI.Weekday]: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        [UI.Week]: "flex w-full mt-2",
        [UI.Day]:
          "h-9 w-9 text-center rounded-md text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-blue-50/50 [&:has([aria-selected])]:bg-blue-50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        [UI.DayButton]: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-blue-100 hover:text-blue-900 rounded-md transition-colors"
        ),
        [SelectionState.range_start]: "bg-blue-600 text-white rounded-l-md rounded-r-none font-semibold",
        [SelectionState.range_end]: "day-range-end bg-blue-600 text-white rounded-r-md rounded-l-none font-semibold",
        [SelectionState.selected]:
          "bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:text-white focus:bg-blue-600 focus:text-white rounded-md",
        [SelectionState.range_middle]: "!bg-blue-100 !text-blue-900 !rounded-none hover:!bg-blue-200",
        [DayFlag.today]: "bg-gray-100 text-gray-900 font-semibold",
        [DayFlag.outside]:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-blue-50 aria-selected:text-blue-900 aria-selected:opacity-70",
        [DayFlag.disabled]: "text-muted-foreground opacity-50 cursor-not-allowed",
        [DayFlag.hidden]: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ ...props }) => <Chevron {...props} />,
        ...(components || {}),
      }}
      {...props}
    />
  );
};

const Chevron = ({ orientation = "left" }) => {
  switch (orientation) {
    case "left":
      return <ChevronLeftIcon className="h-4 w-4" />;
    case "right":
      return <ChevronRightIcon className="h-4 w-4" />;
    case "up":
      return <ChevronUpIcon className="h-4 w-4" />;
    case "down":
      return <ChevronDownIcon className="h-4 w-4" />;
    default:
      return null;
  }
};

Calendar.displayName = "Calendar";
